/**
 * CallContext.js
 * This file implements a React Context for managing call-related state and functionality
 * used Twilio for voice calls and SignalR for real-time notifications.
 */

import React, { createContext, useState, useEffect, useContext } from "react";
import { HubConnectionBuilder } from "@microsoft/signalr";
import { Device } from "@twilio/voice-sdk";
import { getSessionValue } from "../utilities/commonHelpers";
import { apiCall } from "../utilities/apiCall";
import { getBaseURL } from "../components/CallsCenter/useCallService";
import { getClientSettings } from "../services/contactEdit/contactEditService";
// Create the context for managing call-related state and methods
const CallContext = createContext();

// Custom hook to easily access the call context from any component
export const useCallContext = () => useContext(CallContext);

/**
 * CallProvider Component
 * Manages the state and functionality for voice calls using Twilio and SignalR
 * Provides methods for making, receiving, and managing calls
 */
export const CallProvider = ({ children }) => {
  // Initial state object
  const initialState = {
    activeCall: null, // Current active call details
    device: null, // Twilio device instance
    connection: null, // SignalR connection instance
    callStatus: "", // Current status of the call
    callSummary: "", // Summary of the call
    callDuration: 0, // Duration of the call
    isCloudCommunicationEnabled: false, // Feature flag for cloud communication
    connectionState: "disconnected", // Track connection state
    defaultNumber: null, // Default phone number
    baseURL: null, // Base URL
    callSource: "", // Call Source
    inCall: false, // Indicates whether a call is in progress
  };


  // Single state object for all call-related state
  const [state, setState] = useState(initialState);


  // Helper function to update specific state properties
  const updateState = (updates) => {
    setState((prevState) => ({
      ...prevState,
      ...updates,
    }));
  };

  // Initialize SignalR connection based on site settings
  useEffect(() => {
    const initialize = async () => {
      try {
        const [url, clientID] = await Promise.all([
          getBaseURL(),
          getSessionValue("ClientID"),
        ]);
        let URL = url + "/api/cloudcommunication";
        const response = await getClientSettings(clientID);
        if (response?.IsCloudCommunicationEnabled) {
          
            updateState({ isCloudCommunicationEnabled: true, baseURL: URL });
            await fetchDefaultNumber(URL);
            initializeConnection(url);
            initializeDevice(URL);
        }
      } catch (error) {
        console.error("Failed to initialize connection:", error);
      }
    };

    initialize();
    
    // Cleanup function to properly disconnect when component unmounts
    return () => {
      if (state.connection) {
        state.connection.stop();
      }
      if (state.device) {
        state.device.destroy();
      }
    };
  }, []);

 const callEnd=async(callSid)=>
 {
       await apiCall(
        `/DisconnectCall/${callSid}`,
        "GET",
        {},
        { "Access-Control-Allow-Origin": "*" },
        "no-cache",
        "cors",
        {},
        false,
        state.baseURL
      );
 }



  const fetchDefaultNumber = async (baseURL) => {
    if (!baseURL) return;
    try {
      const userID = getSessionValue("UserID");
      const response = await apiCall(
        `/fromnumbers/${userID}`,
        "GET",
        {},
        { "Access-Control-Allow-Origin": "*" },
        "no-cache",
        "cors",
        {},
        false,
        baseURL
      );
      if (response.length > 0) {
        const phoneNumber =
          response.find((item) => item.userID == userID)?.phoneNumber ||
          response.find((item) => item.userID == -1)?.phoneNumber;
        updateState({ defaultNumber: phoneNumber });
      }
    } catch (error) {
      console.error("Failed to fetch default number:", error);
    }
  };
  /**
   * Initializes the SignalR connection for real-time notifications
   */
  const initializeConnection = (baseURL) => {
    const notifierUrl = `${baseURL}/Notifier`;
    const newConnection = new HubConnectionBuilder()
      .withUrl(notifierUrl, {
        withCredentials: false,
      })
      .withAutomaticReconnect(10) // Progressive reconnection delays
      .build();

    // Set up connection state change handler
    newConnection.onreconnecting((error) => {
      console.log("SignalR reconnecting:", error);
      updateState({ connectionState: "reconnecting" });
    });

    newConnection.onreconnected((connectionId) => {
      let reqBody = {
        ConnectionID: state.connection?.ConnectionID,
        Identity: `${getSessionValue("Email")}_${getSessionValue(
          "ClientID"
        )}_${getSessionValue("UserID")}`,
      };
      newConnection.invoke("UserConnected", reqBody);
      updateState({ connection: newConnection });
      console.log("SignalR reconnected:", connectionId);
    });

    newConnection.onclose((error) => {
      console.log("SignalR closed:", error);
      updateState({ connectionState: "disconnected" });
    });

    updateState({ connection: newConnection });
  };

  // Set up SignalR event handlers and connection
  useEffect(() => {
    if (
      state.connection &&
      state.isCloudCommunicationEnabled &&
      state.defaultNumber
    ) {
      // Handle call log notifications
      state.connection.on("callLogNotifier", (data) => {
        if (Notification.requestPermission()) {
          new Notification("Recent Call Transcription", {
            body: "A transcription for your recent call has arrived.",
          });
        }
      });

      // Handle call status updates
      state.connection.on("updateCallStatus", (statusObject) => {
        if(state?.activeCall==null||state?.activeCall?.isIncoming)
        {
           if(statusObject.status=="Answered"||statusObject.status=="Ringing")
           {
             updateState({ callStatus: statusObject?.status });
           }
           if(["Cancelled by system","Busy","No answer","Completed","Cancelled"].includes(statusObject?.status))
           {
             handleEndCall()
           }
          return;
        }
        if(state?.activeCall?.parameters?.CallSid === statusObject?.callSid){
        if (statusObject?.status !== "Cancelled") {
          updateState({ callStatus: statusObject?.status });
        }
        }
        if (statusObject?.recordDuration) {
          updateState({ callDuration: statusObject?.recordDuration });
        }
        // }

        // Play voice announcements if available
        if (statusObject.annoncement) {
          const speech = new SpeechSynthesisUtterance(statusObject.annoncement);
          speech.lang = "en-US";
          speech.rate = 1;
          window.speechSynthesis.speak(speech);
        }
      });

      // Handle call transcription updates
      state.connection.on("SendTranscription", (transcription) => {
        if (transcription.conversationSummary) {
          getSummary(transcription.conversationSummary);
          updateState({ callSummary: transcription.conversationSummary });
        }
      });

      // Start SignalR connection and register user
      state.connection
        .start()
        .then(() => {
          updateState({ connectionState: "connected" });
          let reqBody = {
            ConnectionID: null,
            Identity: `${getSessionValue("Email")}_${getSessionValue(
              "ClientID"
            )}_${getSessionValue("UserID")}`,
          };
          state.connection.invoke("UserConnected", reqBody);
        })
        .catch((err) => {
          console.error("SignalR Connection Error:", err);
          updateState({ connectionState: "error" });
        });
    }
  }, [
    state.connection,
    state.isCloudCommunicationEnabled,
    state.defaultNumber,
  ]);

  const initializeDevice = async (baseURL) => {
    try {
      const resp = await apiCall(
        `/token`,
        "POST",
        {
          UserName: `${getSessionValue("Email")}_${getSessionValue(
            "ClientID"
          )}_${getSessionValue("UserID")}`,
        },
        { "Access-Control-Allow-Origin": "*" },
        "no-cache",
        "cors",
        {},
        false,
        baseURL
      );

      const token = resp; // Ensure token is extracted correctly

      if (!token) {
        console.error("Failed to get token");
        return;
      }

      // Initialize Twilio device with token
      const twilioDevice = new Device(token, {
        codecPreferences: ["opus", "pcmu"],
        closeProtection: true,
        debug: true,
      });

      // Set up Twilio device event handlers
      twilioDevice.on("ready", () => {
        console.log("Twilio Device Ready!");
      });

      twilioDevice.on("error", (error) => {
        console.error("Twilio Device Error:", error);
      });

      // Helper function to handle incoming call state updates
      const handleIncomingCallState = (incomingConnection) => {
        // Set up active call state for incoming call
        const phoneNumber = incomingConnection.parameters.From || "Unknown";
        updateState({
          activeCall: {
            number: phoneNumber,
            name: "Incoming Call",
            isMuted: false,
            isRecording: false,
            isIncoming: true,
            connection: incomingConnection,
          },
          callStatus: "Incoming Call",
          inCall: true,
        });
      };

      // Handle incoming calls
      twilioDevice.on("incoming", (incomingConnection) => {
        handleIncomingCallState(incomingConnection);
      });

      // Register and store the Twilio device
      twilioDevice.register();
      updateState({ device: twilioDevice });
    } catch (error) {
      console.error("Error initializing Twilio device:", error);
    }
  };

  /**
   * Initiates an outgoing call
   * @param {Object} callDetails - Details of the call to be made
   * @returns {Promise} Twilio connection object
   */
  const makeCall = async (callDetails) => {
    if (!state.device) {
      console.error("Twilio device not initialized");
      return;
    }

    if (!state.defaultNumber) {
      console.error("Default number not found");
      return;
    }
    try {
      updateState({ callStatus: "Call Connecting" });

      //replacing characters from the number
      let toNumber=`+${callDetails.number.replace(/\D/g, "")}`;
      // Create new Twilio connection
      const twilioConnection = await state.device.connect({
        params: {
          From: state.defaultNumber,
          ToPhoneNumber: toNumber,
          RequestedUser: {
            UserID: getSessionValue("UserID"),
            Email: getSessionValue("Email"),
          },
        },
      });

      // Set up active call state
      updateState({
        activeCall: {
          ...callDetails,
          isMuted: false,
          isRecording: false,
          connection: twilioConnection,
        },
        inCall: true,
      });

      // Set up connection event handlers
      twilioConnection.on("accept", () => {
        console.log("Call accepted");
      });

      twilioConnection.on("disconnect", () => {
        console.log("Call disconnected");
        handleEndCall();
      });

      twilioConnection.on("reject", () => {
        console.log("Call rejected");
        handleEndCall();
      });

      return twilioConnection;
    } catch (error) {
      console.error("Error making call:", error);
      updateState({ activeCall: null, callStatus: "", inCall: false });
    }
  };

  /**
   * Ends the current active call
   */
  const handleEndCall = () => {
    updateState({ callStatus: "", inCall: false });
    if (state.activeCall && state.activeCall.connection) {
      try {
        if(state.activeCall.isIncoming)
        { 
          if(state.activeCall.connection.parameters?.CallSid)
          {
            callEnd(state.activeCall.connection.parameters.CallSid)
          }
          state.activeCall.connection.reject();
        }
        state.activeCall.connection.disconnect();
      } catch (error) {
        console.error("Error disconnecting call:", error);
      }
    }

    updateState({ activeCall: null, inCall: false });
  };

  /**
   * Accepts an incoming call
   */
  const handleAcceptCall = () => {
    if (
      state.activeCall &&
      state.activeCall.isIncoming &&
      state.activeCall.connection
    ) {
      try {
        state.activeCall.connection.accept();
        updateState({ callStatus: "Answered" });
      } catch (error) {
        console.error("Error accepting call:", error);
      }
    }
  };

  /**
   * Toggles the mute state of the current call
   */
  const toggleMute = () => {
    if (state.activeCall && state.activeCall.connection) {
      const isMuted = !state.activeCall.isMuted;

      try {
        state.activeCall.connection.mute(isMuted);
        updateState({
          activeCall: {
            ...state.activeCall,
            isMuted,
          },
        });
      } catch (error) {
        console.error("Error toggling mute:", error);
      }
    }
  };

  /**
   * Updates the call summary state
   * @param {Object} summary - The call summary to be stored
   */
  const getSummary = (summary) => {
    updateState({ callSummary: summary });
  };

  /**
   * Clears the current call summary
   */
  const clearCallSummary = () => {
    updateState({ callSummary: null });
  };

  const setCallSource = (source) => {
    updateState({ callSource: source });
  };

  // Provide call context values to child components
  return (
    <CallContext.Provider
      value={{
        activeCall: state.activeCall,
        callStatus: state.callStatus,
        callSummary: state.callSummary,
        callDuration: state.callDuration,
        defaultNumber: state.defaultNumber,
        baseURL: state.baseURL,
        isCloudCommunicationEnabled: state.isCloudCommunicationEnabled,
        callSource: state.callSource,
        inCall: state.inCall,
        makeCall,
        handleEndCall,
        handleAcceptCall,
        toggleMute,
        clearCallSummary,
        getSummary,
        setCallSource
      }}
    >
      {children}
    </CallContext.Provider>
  );
};

export default CallContext;
